import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useSiteSettings, formatBusinessHours } from '../contexts/SettingsContext';

const ContactPage: React.FC = () => {
  const { settings } = useSiteSettings();
  const bhLines = formatBusinessHours(settings.businessHoursSchedule);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      setLoading(false);
      setShowAlert(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setShowAlert(false), 5000);
    }, 1000);
  };

  return (
    <div className="bg-light">
      <Container className="py-5">
        {/* Hero Section */}
        <Row className="mb-5">
          <Col>
            <div className="text-center">
              <h1 className="display-4 fw-bold mb-4">Contact Us</h1>
              <p className="lead text-muted">
                We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </div>
          </Col>
        </Row>

        {showAlert && (
          <Row className="mb-4">
            <Col>
              <Alert variant="success" dismissible onClose={() => setShowAlert(false)}>
                <Alert.Heading>Message Sent!</Alert.Heading>
                <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          {/* Contact Information */}
          <Col lg={4} className="mb-5">
            <h3 className="fw-bold mb-4">Get in Touch</h3>
            
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                    <i className="bi bi-geo-alt text-primary fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Address</h6>
                    <p className="text-muted mb-0">
                      {settings.address_street}<br />
                      {settings.address_city}, {settings.address_state} - {settings.address_pincode}<br />
                      {settings.address_country}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                    <i className="bi bi-telephone text-success fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Phone</h6>
                    <p className="text-muted mb-0">{settings.supportPhone}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                    <i className="bi bi-envelope text-info fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Email</h6>
                    <p className="text-muted mb-0">{settings.contactEmail}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {settings.showBusinessHours && (
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                    <i className="bi bi-clock text-warning fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">Business Hours</h6>
                    <p className="text-muted mb-0">
                      {bhLines.map((line, i) => (
                        <span key={i}>{line}{i < bhLines.length - 1 && <br />}</span>
                      ))}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
            )}
          </Col>

          {/* Contact Form */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-5">
                <h3 className="fw-bold mb-4">Send us a Message</h3>
                
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="shipping">Shipping & Returns</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Message <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Please describe how we can help you..."
                    />
                  </Form.Group>
                  
                  <div className="d-grid">
                    <Button 
                      variant="primary" 
                      size="lg" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* FAQ Section */}
        <Row className="mt-5">
          <Col>
            <div className="text-center">
              <h3 className="fw-bold mb-4">Frequently Asked Questions</h3>
              <p className="mb-4">
                Before sending a message, you might find your answer in our FAQ section.
              </p>
              <a href="/faq" className="btn btn-outline-primary">
                View FAQ
              </a>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ContactPage;